/**
 * API endpoints para Scheduling (Agendamiento)
 * Base URL: /api/scheduling/admin/
 */

import { GET, POST, PUT, DELETE } from "../config";

// ==================== CALENDARIOS ====================

export const listCalendars = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/calendars/?page=${page}&page_size=50`;
  if (filters.branch) url += `&branch=${filters.branch}`;
  if (filters.name)
    url += `&name__icontains=${encodeURIComponent(filters.name)}`;
  const response = await GET(url);
  return response.data;
};

export const getCalendar = async (id) => {
  const response = await GET(`scheduling/admin/calendars/${id}/`);
  return response.data;
};

export const createCalendar = async (data) => {
  const response = await POST("scheduling/admin/calendars/", data);
  return response.data;
};

export const updateCalendar = async (id, data) => {
  const response = await PUT(`scheduling/admin/calendars/${id}/`, data);
  return response.data;
};

export const deleteCalendar = async (id) => {
  const response = await DELETE(`scheduling/admin/calendars/${id}/`);
  return response.data;
};

// ==================== RESERVAS (BOOKINGS) ====================

export const listBookings = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/bookings/?page=${page}&page_size=50`;
  if (filters.calendar) url += `&calendar=${filters.calendar}`;
  if (filters.status) url += `&status=${filters.status}`;
  if (filters.date) url += `&start_time__date=${filters.date}`;
  const response = await GET(url);
  return response.data;
};

export const getBooking = async (id) => {
  const response = await GET(`scheduling/admin/bookings/${id}/`);
  return response.data;
};

export const createBooking = async (data) => {
  const response = await POST("scheduling/admin/bookings/", data);
  return response.data;
};

export const updateBooking = async (id, data) => {
  const response = await PUT(`scheduling/admin/bookings/${id}/`, data);
  return response.data;
};

export const deleteBooking = async (id) => {
  const response = await DELETE(`scheduling/admin/bookings/${id}/`);
  return response.data;
};

export const performBookingAction = async (id, action, data = {}) => {
  const response = await POST(`scheduling/admin/bookings/${id}/action/`, {
    action,
    ...data,
  });
  return response.data;
};

export const getBookingsByDate = async (date) => {
  const response = await GET(`scheduling/admin/bookings/by-date/?date=${date}`);
  return response.data;
};

// ==================== REUNIONES (MEETINGS) ====================

export const listMeetings = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/meetings/?page=${page}&page_size=50`;
  if (filters.calendar) url += `&calendar=${filters.calendar}`;
  const response = await GET(url);
  return response.data;
};

export const getMeeting = async (id) => {
  const response = await GET(`scheduling/admin/meetings/${id}/`);
  return response.data;
};

export const createMeeting = async (data) => {
  const response = await POST("scheduling/admin/meetings/", data);
  return response.data;
};

export const updateMeeting = async (id, data) => {
  const response = await PUT(`scheduling/admin/meetings/${id}/`, data);
  return response.data;
};

export const deleteMeeting = async (id) => {
  const response = await DELETE(`scheduling/admin/meetings/${id}/`);
  return response.data;
};

// ==================== TAREAS (TASKS) ====================

export const listTasks = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/tasks/?page=${page}&page_size=50`;
  if (filters.status) url += `&status=${filters.status}`;
  if (filters.assigned_to) url += `&assigned_to=${filters.assigned_to}`;
  const response = await GET(url);
  return response.data;
};

export const getTask = async (id) => {
  const response = await GET(`scheduling/admin/tasks/${id}/`);
  return response.data;
};

export const createTask = async (data) => {
  const response = await POST("scheduling/admin/tasks/", data);
  return response.data;
};

export const updateTask = async (id, data) => {
  const response = await PUT(`scheduling/admin/tasks/${id}/`, data);
  return response.data;
};

export const deleteTask = async (id) => {
  const response = await DELETE(`scheduling/admin/tasks/${id}/`);
  return response.data;
};

// ==================== HORARIOS (SCHEDULES) ====================

export const listSchedules = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/schedules/?page=${page}&page_size=50`;
  if (filters.calendar) url += `&calendar=${filters.calendar}`;
  const response = await GET(url);
  return response.data;
};

export const createSchedule = async (data) => {
  const response = await POST("scheduling/admin/schedules/", data);
  return response.data;
};

export const updateSchedule = async (id, data) => {
  const response = await PUT(`scheduling/admin/schedules/${id}/`, data);
  return response.data;
};

export const deleteSchedule = async (id) => {
  const response = await DELETE(`scheduling/admin/schedules/${id}/`);
  return response.data;
};

// ==================== RECURSOS (RESOURCES) ====================

export const listResources = async (page = 1, filters = {}) => {
  let url = `scheduling/admin/resources/?page=${page}&page_size=50`;
  if (filters.calendar) url += `&calendar=${filters.calendar}`;
  const response = await GET(url);
  return response.data;
};

export const createResource = async (data) => {
  const response = await POST("scheduling/admin/resources/", data);
  return response.data;
};

export const updateResource = async (id, data) => {
  const response = await PUT(`scheduling/admin/resources/${id}/`, data);
  return response.data;
};

export const deleteResource = async (id) => {
  const response = await DELETE(`scheduling/admin/resources/${id}/`);
  return response.data;
};

// ==================== DASHBOARD ====================

export const getDashboard = async (filters = {}) => {
  let url = "scheduling/admin/dashboard/";
  const params = new URLSearchParams();
  if (filters.branch) params.append("branch", filters.branch);
  if (params.toString()) url += `?${params.toString()}`;
  const response = await GET(url);
  return response.data;
};

// ==================== EXPORT ====================

const schedulingApi = {
  calendars: {
    list: listCalendars,
    get: getCalendar,
    create: createCalendar,
    update: updateCalendar,
    delete: deleteCalendar,
  },
  bookings: {
    list: listBookings,
    get: getBooking,
    create: createBooking,
    update: updateBooking,
    delete: deleteBooking,
    performAction: performBookingAction,
    getByDate: getBookingsByDate,
  },
  meetings: {
    list: listMeetings,
    get: getMeeting,
    create: createMeeting,
    update: updateMeeting,
    delete: deleteMeeting,
  },
  tasks: {
    list: listTasks,
    get: getTask,
    create: createTask,
    update: updateTask,
    delete: deleteTask,
  },
  schedules: {
    list: listSchedules,
    create: createSchedule,
    update: updateSchedule,
    delete: deleteSchedule,
  },
  resources: {
    list: listResources,
    create: createResource,
    update: updateResource,
    delete: deleteResource,
  },
  getDashboard,
};

export default schedulingApi;

